/**
 * Ingest's one job picker, for Rigs and Handheld: the approved mockup
 * mockups/ingest/job-list-v2.html (claude/ingest-job-list-mockup), built
 * with Ivin's picks of 2026-10-09/10: the compact list (layout A) on a
 * desktop, cards by day (layout B) on a phone; a 14-day quiet period; guest
 * pages keep a job listed for the first 30 days after their email; Archive.
 *
 * The rule is src/lib/ingestPicker.js; the jobs, Pin and Archive come from
 * the server (src/lib/ingestList.js). Not for an event account: its list is
 * its assigned jobs (RigV2App's EventJobPick), as before.
 *
 * Archive only tidies this shared list: nothing is deleted, the status does
 * not change, guest pages and delivery carry on. Undo for a moment after; an
 * archived job comes back on its own when something needs attention; Find
 * reaches every job, archived and trashed ones included.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./ingestJobPicker.css";
import {
  QUIET_DAYS, GUEST_PAGE_DAYS, NEAR_AHEAD_DAYS, NEAR_BEHIND_DAYS, byText, dayNumberOf, findJobs, groupsA, groupsB, jobsForScreen,
  nyDay, putBackMode, quietText, shortWhy, short, statesOf, statusLabel, tidyList, weekdayOf, whenOf, whoOf,
} from "../../../lib/ingestPicker.js";
import { ingestList as defaultApi, mutateList, useIngestList, useMinuteClock } from "../../../lib/ingestList.js";

export const PHONE_MAX = 560;
const useIsoLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
// Job ids are plain ([A-Za-z0-9_-]); quotes and backslashes escaped anyway.
const cssq = (v) => String(v).replace(/["\\]/g, "\\$&");

/* ── icons (the mockup's) ── */
const PIN = (p) => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true" {...p}><path d="M6 1.8h4l-.6 4.4 2.6 2.3v1.2H4v-1.2l2.6-2.3z" /><path d="M8 9.7v4.6" strokeLinecap="round" /></svg>;
const INFO = () => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="8" cy="8" r="6.25" /><path d="M8 7.2v4" strokeLinecap="round" /><circle cx="8" cy="5" r="0.5" fill="currentColor" stroke="none" /></svg>;
const ARCHIVE = () => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="2.75" width="12" height="3" rx="0.8" /><path d="M3 5.75v6.5a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-6.5" /><path d="M6.5 8.75h3" strokeLinecap="round" /></svg>;
const BROOM = () => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.5 2 6.8 7.6" /><path d="M4.3 7.2c1.8-.9 3.9-.4 5 1.4L5.9 14c-1.4-.4-2.6-1.4-3.2-2.8" /><path d="M4.4 11.2 8 9.7" /></svg>;
const GLASS = () => <svg className="ijp-glass" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><circle cx="7" cy="7" r="5" /><path d="M11 11l3.5 3.5" /></svg>;
const ICON_EMPTY = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><rect x="3" y="4" width="14" height="12" rx="2" /><path d="M3 8h14M7 4V2.5M13 4V2.5" strokeLinecap="round" /></svg>;
const ICON_WARN = () => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="10" cy="10" r="7.25" /><path d="M10 6.5v4.5" strokeLinecap="round" /><circle cx="10" cy="13.6" r="0.6" fill="currentColor" stroke="none" /></svg>;

export const UNPIN_FIRST = "Unpin first: a pinned job stays in Ingest.";
export const TIDY_MAX = 100;
// The In Ingest control's current choice: a job that came back on its own is Automatic again.
const modeOf = (job, st) => (st?.back ? "auto" : (job.list?.mode ?? "auto"));
const ARCHIVE_MEANS = "That takes it off this list only, for everyone: everything is kept, and the job is unchanged in the rest of the Suite.";
const SUBTITLE = {
  rigs: "Capture with the camera rig. Same list as Handheld, plus guest jobs and the studio walk-ins.",
  handheld: "Bring files in after the shoot. Same list as Rigs, without guest jobs and the studio walk-ins.",
};

/** A sentence from the rule ([string | { b }]) as text and bold. */
const Sentence = ({ parts }) => parts.map((p, i) => (typeof p === "string" ? <React.Fragment key={i}>{p}</React.Fragment> : <b key={i}>{p.b}</b>));
const Why = ({ why }) => why.map((s, i) => <React.Fragment key={i}>{i > 0 && " "}<Sentence parts={s} /></React.Fragment>);
const plainWhy = (why) => why.map((s) => s.map((p) => (typeof p === "string" ? p : p.b)).join("")).join(" ");

function StatusChips({ job }) {
  return (
    <span className="ijp-chips">
      {job.trashed ? <span className="ijp-chip trash">In trash</span> : <span className={`ijp-chip ${job.status}`}>{statusLabel(job.status)}</span>}
      {job.paid ? <span className="ijp-chip money paid">Paid</span> : job.invoiced ? <span className="ijp-chip money">Invoiced</span> : null}
    </span>
  );
}

const n = (k, one, many = `${one}s`) => `${k} ${k === 1 ? one : many}`;
/** Every badge (cards, the Why panel); `onlyOpen` keeps what is open now (compact rows). */
export function signalsOf(job, { onlyOpen = false } = {}) {
  const s = [];
  if (!onlyOpen && job.rigCaptures) s.push({ t: n(job.rigCaptures, "rig capture"), c: "" });
  if (!onlyOpen && job.handheld) s.push({ t: n(job.handheld, "handheld subject"), c: "" });
  if (job.review) s.push({ t: `${job.review} waiting for review`, c: "hot" });
  if (job.failed) s.push({ t: `${job.failed} processing failed`, c: "hot" });
  if (job.processing) s.push({ t: `${job.processing} processing`, c: "proc" });
  if (job.held) s.push({ t: `${job.held} processing held`, c: "proc" });
  if (job.unfinished) s.push({ t: `${n(job.unfinished, "capture")} not finished`, c: "hot" });
  if (job.guestPages) s.push({ t: `${n(job.guestPages, "guest page")} live`, c: "live" });
  if (job.registrationOpen) s.push({ t: "Registration open", c: "live" });
  if (!s.length && !onlyOpen) s.push({ t: "Nothing captured yet", c: "none" });
  return s;
}
function Signals({ job, onlyOpen = false }) {
  const s = signalsOf(job, { onlyOpen });
  if (!s.length) return null;
  return <span className="ijp-sig">{s.map((x) => <span key={x.t} className={x.c}>{x.t}</span>)}</span>;
}

function highlight(text, q) {
  const s = String(text ?? "");
  if (!q) return s;
  const i = s.toLowerCase().indexOf(q);
  if (i < 0) return s;
  return <>{s.slice(0, i)}<mark>{s.slice(i, i + q.length)}</mark>{s.slice(i + q.length)}</>;
}

/* ── the toast: a short note; with Undo it stays longer and waits while hovered or focused ── */
function Toast({ toast, onDone }) {
  const ref = useRef(null);
  const timer = useRef(0);
  const left = useRef(0);
  const at = useRef(0);
  const hold = useRef({ hover: false, focus: false, paused: false });
  const show = Boolean(toast);
  const arm = useCallback((ms) => {
    clearTimeout(timer.current);
    hold.current.paused = false;
    at.current = Date.now();
    left.current = ms;
    timer.current = setTimeout(onDone, ms);
  }, [onDone]);
  useEffect(() => {
    if (!toast) return undefined;
    const ms = toast.undo ? 8000 : 2600;
    if (hold.current.hover || hold.current.focus) { left.current = ms; clearTimeout(timer.current); hold.current.paused = true; } else arm(ms);
    return () => clearTimeout(timer.current);
  }, [toast, arm]);
  useEffect(() => { if (ref.current) ref.current.inert = !show; }, [show]);
  const pause = () => {
    if (!show || hold.current.paused) return;
    clearTimeout(timer.current);
    hold.current.paused = true;
    left.current = Math.max(1500, left.current - (Date.now() - at.current));
  };
  const resume = () => { if (show && hold.current.paused && !hold.current.hover && !hold.current.focus) arm(left.current); };
  return (
    <div
      ref={ref} className={`ijp-toast${show ? " show" : ""}${toast?.undo ? " has-undo" : ""}`} role="status" aria-live="polite"
      onMouseEnter={() => { hold.current.hover = true; pause(); }}
      onMouseLeave={() => { hold.current.hover = false; resume(); }}
      onFocus={() => { hold.current.focus = true; pause(); }}
      onBlur={() => { hold.current.focus = false; resume(); }}
    >
      {toast && <span>{toast.msg}</span>}
      {toast?.undo && (
        <button type="button" className="ijp-undo" onClick={() => { const f = toast.undo; clearTimeout(timer.current); onDone(); f(); }}>Undo</button>
      )}
    </div>
  );
}

/* ── swipe left on a phone row to archive (pointer events; a vertical drag scrolls) ── */
function Swipe({ job, pinned, enabled, onArchive, onPinnedSwipe, children }) {
  const wrap = useRef(null);
  const sw = useRef(null);
  const swallow = useRef(false);
  const [cls, setCls] = useState("");
  const rowOf = () => wrap.current?.querySelector(".ijp-row, .ijp-card");
  const end = (e) => {
    const s = sw.current;
    if (!s || e.pointerId !== s.pid) return;
    sw.current = null;
    if (s.axis !== "x") return;
    if (s.dx < -16) { swallow.current = true; setTimeout(() => { swallow.current = false; }, 300); }
    const row = rowOf();
    if (s.ready && e.type !== "pointercancel") {
      setCls("");
      if (row) row.style.transform = "";
      onArchive(job);
      return;
    }
    if (pinned && s.dx < -24) onPinnedSwipe();
    setCls("settle");
    if (row) row.style.transform = "";
  };
  return (
    <div
      ref={wrap} className={`ijp-swipe${pinned ? " pinned-swipe" : ""}${cls ? ` ${cls}` : ""}`} data-swipe={job.id}
      onPointerDown={(e) => {
        if (!enabled || sw.current || (e.pointerType === "mouse" && e.button !== 0)) return;
        sw.current = { pid: e.pointerId, x0: e.clientX, y0: e.clientY, dx: 0, axis: null, ready: false };
        setCls("");
      }}
      onPointerMove={(e) => {
        const s = sw.current;
        if (!s || e.pointerId !== s.pid) return;
        const dx = e.clientX - s.x0;
        const dy = e.clientY - s.y0;
        if (!s.axis) {
          if (Math.abs(dy) >= 8 && Math.abs(dy) >= Math.abs(dx)) { s.axis = "y"; return; }
          if (dx > -16) return;
          s.axis = "x";
          try { wrap.current.setPointerCapture(e.pointerId); } catch { /* no capture */ }
          setCls("swiping");
        }
        if (s.axis !== "x") return;
        const limit = pinned ? 64 : 180;
        const d = Math.min(0, dx);
        s.dx = d < -limit ? -limit - (-d - limit) * 0.2 : d;
        const ready = !pinned && s.dx <= -96;
        if (ready !== s.ready) { s.ready = ready; setCls(ready ? "swiping ready" : "swiping"); }
        const row = rowOf();
        if (row) row.style.transform = `translateX(${s.dx}px)`;
      }}
      onPointerUp={end}
      onPointerCancel={end}
      onClickCapture={(e) => { if (swallow.current) { swallow.current = false; e.preventDefault(); e.stopPropagation(); } }}
    >
      <div className="ijp-swipe-under" aria-hidden="true">{pinned ? "Unpin first" : <><ARCHIVE />Archive</>}</div>
      {children}
    </div>
  );
}

/* ── the picker ── */
export default function IngestJobPicker({
  screen = "rigs", onPick, onExit, selectedId = null, extra = null, api = defaultApi, listHook = useIngestList,
  forcePhone = null, mutate = mutateList,
}) {
  const { data, loading, error, reload } = listHook({ load: api.list });
  const now = useMinuteClock();
  const today = nyDay(now);
  const ctx = useMemo(() => ({ today }), [today]);
  const jobs = useMemo(() => jobsForScreen(data?.jobs, screen), [data, screen]);
  const states = useMemo(() => statesOf(jobs, ctx), [jobs, ctx]);
  const canEdit = Boolean(data?.canEdit);

  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const countRef = useRef(null);
  const [phoneW, setPhoneW] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= PHONE_MAX : false));
  const phone = forcePhone ?? phoneW;
  useIsoLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver !== "function") return undefined;
    const ro = new ResizeObserver(() => setPhoneW(el.clientWidth <= PHONE_MAX));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [q, setQ] = useState("");
  const [ruleOpen, setRuleOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [tidyOpen, setTidyOpen] = useState(false);
  const [why, setWhy] = useState(() => new Set());
  const [toast, setToast] = useState(null);
  const [focusNext, setFocusNext] = useState(null); // { act, id }
  const clearToast = useCallback(() => setToast(null), []);

  // "/" jumps to Find (not while typing anywhere); Escape clears Find or closes the tidy-up.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) { e.preventDefault(); searchRef.current?.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Focus after a redraw: the control asked for, else its job's "Why here?",
  // else the count line. ifLost: only when the focused control went away
  // with the redraw (never taken from where the person moved meanwhile).
  useEffect(() => {
    if (!focusNext) return;
    if (focusNext.ifLost && !lostFocus()) { setFocusNext(null); return; }
    const root = rootRef.current;
    const sel = (s) => root?.querySelector(s);
    const v = focusNext.v ? `[data-v="${cssq(focusNext.v)}"]` : "";
    const el = (focusNext.act && sel(`[data-act="${focusNext.act}"][data-id="${cssq(focusNext.id ?? "")}"]${v}`))
      || (focusNext.id && sel(`[data-act="why"][data-id="${cssq(focusNext.id)}"]`))
      || (focusNext.fallback === "search" ? searchRef.current : countRef.current);
    el?.focus();
    setFocusNext(null);
  }, [focusNext]);
  const lostFocus = () => {
    const a = document.activeElement;
    return !a || a === document.body || !rootRef.current?.contains(a);
  };

  const mine = (mode) => ({ mode, byName: "you", byMe: true, at: Date.now() });

  /**
   * Optimistic: shows the change at once (shown, else the mode as yours) and
   * sends it after any change before it (src/lib/ingestList.js mutateList).
   * True when it was saved and is still the latest change to its jobs. A
   * refusal puts `before` back and reads the list again; a refusal or answer
   * to a change made old by a later one says nothing.
   */
  const send = useCallback(async (changes, before, shown = null) => {
    const optimistic = shown ?? Object.fromEntries(changes.map((c) => [c.jobId, c.mode === "auto" ? null : mine(c.mode)]));
    const r = await mutate(changes, { shown: optimistic, before, send: api.setState });
    if (!r.ok && r.current && !r.aborted) {
      // (mutateList has the list read again.)
      const e = r.error;
      setToast({ msg: e?.message ? `Not saved: ${e.message}` : "Not saved. Check the connection and try again." });
    }
    return r.ok && r.current;
  }, [api, mutate]);

  const byId = useMemo(() => new Map(jobs.map((j) => [j.id, j])), [jobs]);
  const beforeOf = (list) => Object.fromEntries(list.map((j) => [j.id, j.list ?? null]));

  const archive = useCallback(async (list) => {
    if (!canEdit || !list.length) return;
    if (list.some((j) => j.list?.mode === "pin")) { setToast({ msg: UNPIN_FIRST }); return; }
    const before = beforeOf(list);
    const keyboard = !lostFocus();
    const saving = send(list.map((j) => ({ jobId: j.id, mode: "archive" })), before);
    // The row goes at once (optimistic): focus to the count line if it was on it.
    if (keyboard) setFocusNext({ fallback: "count", ifLost: true });
    const ok = await saving;
    if (!ok) return;
    const what = list.length === 1 ? `${list[0].name} archived` : `${list.length} jobs archived`;
    setToast({
      msg: `${what}. Off this list only; everything is kept.`,
      undo: async () => {
        // Undo takes back this archive on the server: the job's earlier state
        // (an earlier archive, for a job that had come back), or none.
        const saving = send(list.map((j) => ({ jobId: j.id, mode: "undo" })), Object.fromEntries(list.map((j) => [j.id, mine("archive")])), before);
        // The job is back at once: focus to its Archive button, if the Undo button (gone with the toast) had it.
        setFocusNext({ act: "archive", id: list[0].id, fallback: "search", ifLost: true });
        const back = await saving;
        if (back) setToast({ msg: list.length === 1 ? `${list[0].name} is back in Ingest.` : `${list.length} jobs are back in Ingest.` });
      },
    });
  }, [canEdit, send]);

  const setMode = useCallback(async (job, mode, { focus = "pin", v } = {}) => {
    if (!canEdit) return;
    const cur = modeOf(job, states.get(job.id));
    if (mode === cur) return;
    if (mode === "archive") { archive([job]); return; }
    const keyboard = !lostFocus();
    const saving = send([{ jobId: job.id, mode }], beforeOf([job]));
    // The job moves group at once (or leaves the list): focus follows it.
    if (keyboard) setFocusNext({ act: focus, id: job.id, v, fallback: "count", ifLost: true });
    const ok = await saving;
    if (!ok) return;
    const after = statesOf([{ ...job, list: mode === "auto" ? null : mine(mode) }], ctx).get(job.id);
    setToast({
      msg: mode === "pin"
        ? `${job.name} is pinned. It stays in Ingest until you unpin it.`
        : `${job.name} is back on the rule: ${after.on ? "it stays in Ingest" : "it leaves Ingest"}.`,
    });
  }, [canEdit, archive, send, ctx, states]);

  const putBack = useCallback(async (job) => {
    if (!canEdit) return;
    const st = states.get(job.id);
    const mode = putBackMode(st);
    const keyboard = !lostFocus();
    const saving = send([{ jobId: job.id, mode }], beforeOf([job]));
    if (keyboard) setFocusNext({ act: "archive", id: job.id, fallback: "count", ifLost: true });
    const ok = await saving;
    if (!ok) return;
    setToast({ msg: mode === "auto" ? `${job.name} is back in Ingest.` : `${job.name} is back in Ingest, pinned (the rule alone would leave it out).` });
  }, [canEdit, states, send]);

  const toggleWhy = (id) => setWhy((s) => { const x = new Set(s); if (x.has(id)) x.delete(id); else x.add(id); return x; });

  /* ── pieces ── */
  const openProps = (j) => ({ "data-act": "open", "data-id": j.id, "aria-current": selectedId === j.id ? "true" : undefined, onClick: () => onPick(j.id) });
  const whyBtn = (j) => (
    <button type="button" className="ijp-ibtn ijp-why" data-act="why" data-id={j.id} aria-expanded={why.has(j.id)}
      aria-controls={why.has(j.id) ? `ijp-why-${j.id}` : undefined} aria-label={`Why is ${j.name} here?`} title="Why here?" onClick={() => toggleWhy(j.id)}>
      <INFO />
    </button>
  );
  const pinBtn = (j) => {
    const pinned = j.list?.mode === "pin";
    return (
      <button type="button" className="ijp-ibtn ijp-pinbtn" data-act="pin" data-id={j.id} aria-pressed={pinned}
        aria-label={`${pinned ? "Unpin" : "Pin"} ${j.name}`} title={pinned ? "Unpin (back to automatic)" : "Pin: keep in Ingest"}
        onClick={() => setMode(j, pinned ? "auto" : "pin", { focus: "pin" })}>
        <PIN />
      </button>
    );
  };
  const arcBtn = (j, iconOnly = false) => {
    const pinned = j.list?.mode === "pin";
    return (
      <button type="button" className={`ijp-ibtn ijp-arcbtn${iconOnly ? " icon" : ""}`} data-act="archive" data-id={j.id}
        aria-disabled={pinned ? "true" : undefined}
        aria-label={pinned ? `Archive ${j.name} (unpin first)` : `Archive ${j.name}`}
        title={pinned ? "Unpin first" : "Archive: take it off this list (nothing else changes)"}
        onClick={() => (pinned ? setToast({ msg: UNPIN_FIRST }) : archive([j]))}>
        <ARCHIVE /><span className="ijp-w">Archive</span>
      </button>
    );
  };
  const putBackBtn = (j) => (
    <button type="button" className="ijp-btn ijp-putback" data-act="putback" data-id={j.id} aria-label={`Put ${j.name} back in Ingest`} onClick={() => putBack(j)}>Put back</button>
  );
  const actions = (j, st, iconOnly) => (
    <>
      {whyBtn(j)}
      {canEdit && (st.src === "archive" ? putBackBtn(j) : <>{pinBtn(j)}{arcBtn(j, iconOnly)}</>)}
    </>
  );

  const overrideSeg = (j) => {
    const cur = modeOf(j, states.get(j.id));
    const b = (v, t) => {
      const blocked = v === "archive" && cur === "pin";
      return (
        <button type="button" data-act="ovr" data-id={j.id} data-v={v} aria-pressed={cur === v}
          aria-disabled={blocked ? "true" : undefined} title={blocked ? "Unpin first" : undefined}
          onClick={() => { if (blocked) setToast({ msg: UNPIN_FIRST }); else setMode(j, v, { focus: "ovr", v }); }}>{t}</button>
      );
    };
    return <span className="ijp-seg" role="group" aria-label={`In Ingest for ${j.name}`}>{b("auto", "Automatic")}{b("pin", "Pinned")}{b("archive", "Archived")}</span>;
  };

  const whyBox = (j, st, withSignals = true) => {
    let because;
    if (st.src === "pin") {
      because = <><b>Pinned by {byText(j.list)}</b>. It stays until someone unpins it. {st.a.on ? "The rule alone would also show it." : <>The rule alone would leave it out: <Why why={st.a.why} /></>}</>;
    } else if (st.src === "archive") {
      because = <><b>Archived by {byText(j.list)}</b>. {ARCHIVE_MEANS} It comes back on its own if something needs attention. {st.a.on ? <>The rule alone would show it: <Why why={st.a.why} /></> : "The rule alone would also leave it out."}</>;
    } else if (st.back) {
      because = <><b>Back since {short(nyDay(st.back.at))}: {st.back.note}</b> (archived by {byText(j.list)}, then something needed attention). Here automatically because: <Why why={st.a.why} /></>;
    } else {
      because = <><b>Here automatically</b> because: <Why why={st.a.why} /></>;
    }
    return (
      <div className="ijp-whybox" id={`ijp-why-${j.id}`}>
        <div>{because}</div>
        {withSignals && <Signals job={j} />}
        {canEdit && <div className="ijp-ovr"><span className="ijp-lbl">In Ingest</span>{overrideSeg(j)}</div>}
      </div>
    );
  };

  const wrapSwipe = (j, st, inner) => (st.src === "archive" || !canEdit ? inner : (
    <Swipe key={j.id} job={j} pinned={j.list?.mode === "pin"} enabled={phone} onArchive={(x) => archive([x])} onPinnedSwipe={() => setToast({ msg: UNPIN_FIRST })}>
      {inner}
    </Swipe>
  ));

  const rowA = (j) => {
    const st = states.get(j.id);
    const w = whenOf(j, today);
    const reason = shortWhy(j, st, today);
    return wrapSwipe(j, st, (
      <div className={`ijp-row${selectedId === j.id ? " sel" : ""}`} key={j.id}>
        <div className="ijp-line">
          <button type="button" className="ijp-open" {...openProps(j)}>
            <span className="ijp-nm"><b>{j.name}</b>{j.what && <small>{j.what}</small>}</span>
            <span className="ijp-when">
              <span className={`ijp-d${w.past ? " past" : ""}`}>{w.text}</span>
              <span className="ijp-meta"><span className="ijp-id">{j.jobId}</span><span className="ijp-sep">·</span>
                <span className={st.back ? "ijp-backnote" : st.src === "archive" ? "ijp-arcby" : ""}>{reason}</span></span>
            </span>
            <span className="ijp-st"><StatusChips job={j} /><Signals job={j} onlyOpen /></span>
          </button>
          <span className="ijp-acts">{actions(j, st, false)}</span>
        </div>
        {why.has(j.id) && whyBox(j, st)}
      </div>
    ));
  };

  const cardB = (j) => {
    const st = states.get(j.id);
    const w = whenOf(j, today);
    const reasonCls = st.src === "pin" ? " pin" : st.back ? " ret" : st.src === "archive" ? " arc" : "";
    return wrapSwipe(j, st, (
      <div className={`ijp-card${selectedId === j.id ? " sel" : ""}`} key={j.id}>
        <button type="button" className="ijp-top" {...openProps(j)}>
          <span className={`ijp-d${w.past ? " past" : ""}`}>{w.text}<span className="ijp-id">{j.jobId}</span></span>
          <b>{j.name}</b>
          {j.what && <small>{j.what}</small>}
        </button>
        <div className="ijp-mid"><StatusChips job={j} /><Signals job={j} /></div>
        <div className="ijp-bot"><span className={`ijp-reason${reasonCls}`}>{shortWhy(j, st, today)}</span>{actions(j, st, true)}</div>
        {why.has(j.id) && whyBox(j, st, false)}
      </div>
    ));
  };

  const item = phone ? cardB : rowA;
  const listCls = phone ? "ijp-cards" : "ijp-list";

  const groupHead = (g) => {
    if (phone && (g.day || g.kind === "hand")) {
      return (
        <div className="ijp-group-h day">
          {g.day
            ? <span className={`ijp-datebox ${g.kind}`} aria-hidden="true"><span className="ijp-wd">{weekdayOf(g.day)}</span><span className="ijp-dn">{dayNumberOf(g.day)}</span></span>
            : <span className="ijp-datebox hand" aria-hidden="true"><PIN width="18" height="18" /></span>}
          <span className="ijp-tt"><h2 className="ijp-t">{g.title} <span className="ijp-count">{g.jobs.length}</span></h2><span className="ijp-hint">{g.hint}</span></span>
        </div>
      );
    }
    return <div className="ijp-group-h"><h2 className="ijp-t">{g.title}</h2><span className="ijp-count">{g.jobs.length}</span><span className="ijp-hint">{g.hint}</span></div>;
  };

  /* ── what the body shows ── */
  const shown = jobs.filter((j) => states.get(j.id)?.on);
  const groups = phone ? groupsB(jobs, ctx, states) : groupsA(jobs, ctx, states);
  const term = q.trim();
  const busy = !data && (loading || error);

  let body;
  if (!data && loading) {
    body = (
      <>
        <p className="ijp-sr" role="status">Loading the job list</p>
        <div className="ijp-skel" aria-busy="true">
          <div className="ijp-bar h" />
          {[0, 1, 2].map((i) => <div className="ijp-skrow" aria-hidden="true" key={i}><div><div className="ijp-bar" style={{ width: "70%" }} /><div className="ijp-bar" /></div><div><div className="ijp-bar" style={{ width: "50%" }} /><div className="ijp-bar" /></div><div className="ijp-bar" style={{ width: 80 }} /></div>)}
        </div>
      </>
    );
  } else if (!data && error) {
    body = (
      <div className="ijp-state error" role="alert">
        <span className="ijp-ic"><ICON_WARN /></span>
        <h2>The job list did not load</h2>
        <p>{errorWords(error)}</p>
        <button type="button" className="ijp-btn" data-act="retry" onClick={() => { reload(); setFocusNext({ fallback: "search" }); }}>Try again</button>
      </div>
    );
  } else if (term) {
    const lower = term.toLowerCase();
    const { hits, total } = findJobs(jobs, term, states);
    body = !hits.length ? (
      <div className="ijp-state" role="status">
        <span className="ijp-ic"><ICON_EMPTY /></span>
        <h2>No job matches “{term}”</h2>
        <p>Find looks through every job, in Ingest or not, by name, job id and what it is.</p>
      </div>
    ) : (
      <>
        <div className="ijp-group-h"><h2 className="ijp-t">Every job</h2><span className="ijp-count">{total}</span>
          <span className="ijp-hint">{total > hits.length ? `the first ${hits.length}: type more to narrow it` : "jobs in Ingest first, then the rest; newest activity first in each"}</span></div>
        <div className="ijp-list">
          {hits.map((j) => {
            const st = states.get(j.id);
            const inChip = j.trashed ? null : st.on
              ? <span className="ijp-chip ingest-on">In Ingest{st.src === "pin" ? " (pinned)" : ""}</span>
              : <span className="ijp-chip ingest-off">{st.src === "archive" ? "Archived from Ingest" : "Not in Ingest"}</span>;
            const line = j.trashed ? "In the trash: restore it in Jobs to use it here."
              : st.src === "archive" ? <><span className="ijp-arcby">Archived by {byText(j.list)}.</span> Off this list only; everything is kept.</>
                : st.on ? `${shortWhy(j, st, today)}.` : plainWhy(st.a.why);
            const last = typeof j.lastActivity === "number" ? ` · last activity ${short(nyDay(j.lastActivity))}` : "";
            return (
              <div key={j.id} className={`ijp-res${st.on ? "" : " off"}${selectedId === j.id ? " sel" : ""}`}>
                <div className="ijp-nm">
                  <b>{highlight(j.name, lower)}</b>
                  <small>{j.what && <>{highlight(j.what, lower)} · </>}<span className="ijp-id">{highlight(j.jobId, lower)}</span> · {whenOf(j, today).text}{last}</small>
                  <div className="ijp-tags"><StatusChips job={j} /> {inChip}</div>
                  <small>{line}</small>
                </div>
                <div className="ijp-acts">
                  {j.trashed
                    ? <button type="button" className="ijp-btn" disabled title="Restore it in Jobs first" aria-label={`Open ${j.name} (in the trash)`}>Open</button>
                    : <button type="button" className="ijp-btn primary" {...openProps(j)} aria-label={`Open ${j.name}`}>Open</button>}
                  {!j.trashed && canEdit && (st.src === "archive" ? putBackBtn(j)
                    : j.list?.mode === "pin"
                      ? <button type="button" className="ijp-btn" data-act="ovr" data-id={j.id} data-v="auto" aria-label={`Unpin ${j.name}`} onClick={() => setMode(j, "auto", { focus: "ovr", v: "pin" })}>Unpin</button>
                      : <button type="button" className="ijp-btn" data-act="ovr" data-id={j.id} data-v="pin" aria-label={`Pin ${j.name} in Ingest`} onClick={() => setMode(j, "pin", { focus: "ovr", v: "auto" })}>Pin in Ingest</button>)}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  } else if (!groups.length) {
    body = (
      <div className="ijp-state" role="status">
        <span className="ijp-ic"><ICON_EMPTY /></span>
        <h2>No job is in Ingest right now</h2>
        <p>Nothing has a shoot day near, recent activity or work still open. Any job can be opened with Find{canEdit ? ", or pinned in from its Find result" : ""}.</p>
      </div>
    );
  } else {
    body = groups.map((g) => (
      <section className="ijp-group" aria-label={g.title} key={g.key}>
        {groupHead(g)}
        <div className={listCls}>{g.jobs.map((j) => item(j))}</div>
      </section>
    ));
  }

  /* ── foot: tidy-up, archived, the rest ── */
  let foot = null;
  if (data && !term) {
    const arc = jobs.filter((j) => states.get(j.id)?.src === "archive");
    const off = jobs.filter((j) => { const st = states.get(j.id); return !st.on && st.src === "auto"; }).length;
    const trash = jobs.filter((j) => j.trashed).length;
    // At most TIDY_MAX at once (the server takes 100 changes a call); the rest are offered next time.
    const tidyAll = canEdit ? tidyList(jobs, ctx, states) : [];
    const tidy = tidyAll.slice(0, TIDY_MAX);
    const one = tidy.length === 1;
    foot = (
      <div className="ijp-foot">
        {tidy.length > 0 && !tidyOpen && (
          <div className="ijp-tidy">
            <span className="ijp-ic"><BROOM /></span>
            <span className="ijp-t">{one ? "1 job has" : `${tidy.length} jobs have`} been quiet for {QUIET_DAYS} days.
              <small>In the list only because of guest pages, review or processing left open; nothing has happened on {one ? "it" : "them"}.</small></span>
            <button type="button" className="ijp-btn" data-act="tidy" data-id="" onClick={() => { setTidyOpen(true); setFocusNext({ act: "tidyok", id: "" }); }}>Archive {one ? "it" : "them"}</button>
          </div>
        )}
        {tidy.length > 0 && tidyOpen && (
          <section className="ijp-confirm" aria-labelledby="ijp-tidy-h" onKeyDown={(e) => { if (e.key === "Escape") { setTidyOpen(false); setFocusNext({ act: "tidy", id: "" }); } }}>
            <h2 id="ijp-tidy-h">Archive {one ? "this quiet job" : `these ${tidy.length} quiet jobs`}?</h2>
            {tidyAll.length > tidy.length && <p>The {tidy.length} quietest of {tidyAll.length}; the rest are offered after.</p>}
            <p>{one ? "It leaves" : "They leave"} this list for everyone. Nothing is deleted or changed elsewhere in the Suite, and each comes back on its own if something needs attention. Undo stays available for a moment.</p>
            <ul>{tidy.map((j) => (
              <li key={j.id}><span><b>{j.name}</b><small>{[j.what, statusLabel(j.status), shortWhy(j, states.get(j.id), today)].filter(Boolean).join(" · ")}</small></span><span className="ijp-q">{quietText(j, today)}</span></li>
            ))}</ul>
            <div className="ijp-btns">
              <button type="button" className="ijp-btn primary" data-act="tidyok" data-id="" onClick={() => { setTidyOpen(false); archive(tidy); }}>Archive {one ? "1 job" : `${tidy.length} jobs`}</button>
              <button type="button" className="ijp-btn" data-act="tidyno" data-id="" onClick={() => { setTidyOpen(false); setFocusNext({ act: "tidy", id: "" }); }}>Keep {one ? "it" : "them"}</button>
            </div>
          </section>
        )}
        {arc.length > 0 && (
          <div className="ijp-archived-h">
            <span>{arc.length} archived</span>{" "}
            <button type="button" className="ijp-linkbtn" data-act="showarchived" aria-expanded={showArchived} aria-controls="ijp-archived-list" onClick={() => setShowArchived((v) => !v)}>{showArchived ? "Hide" : "Show"}</button>
            {showArchived && <span>· off this list only{canEdit ? "; one tap puts a job back" : ""}</span>}
          </div>
        )}
        {showArchived && arc.length > 0 && (
          <div id="ijp-archived-list" className={listCls}>{arc.map((j) => item(j))}</div>
        )}
        <div>{off} other {off === 1 ? "job is" : "jobs are"} not in Ingest (the rule leaves them out) and {trash} {trash === 1 ? "is" : "are"} in the trash. Find reaches all of them.</div>
      </div>
    );
  }

  const selectedJob = selectedId ? byId.get(selectedId) : null;

  return (
    <div className={`ijp${phone ? " phone" : ""}`} ref={rootRef} data-layout={phone ? "B" : "A"}>
      <div className="ijp-topbar">
        <div className="ijp-brand">New York Capture<span className="ijp-sub">Suite · Ingest</span></div>
        <div className="ijp-topjob"><span className="ijp-lbl">Job</span><span className="ijp-topjob-name">{selectedJob ? selectedJob.name : "none yet"}</span></div>
        {onExit && <button type="button" className="ijp-back" onClick={onExit}>← Ingest</button>}
      </div>
      <div className="ijp-page">
        <header className="ijp-head">
          <p className="ijp-eyebrow">{screen === "handheld" ? "Handheld" : "Rigs"}</p>
          <h1 className="ijp-title">Pick a job</h1>
          <p className="ijp-subtitle">{SUBTITLE[screen] ?? SUBTITLE.rigs}</p>
          {extra && <div className="ijp-extra">{extra}</div>}
        </header>

        <div className="ijp-searchbar">
          <div className="ijp-search">
            <GLASS />
            <input
              ref={searchRef} type="search" autoComplete="off" spellCheck={false} value={q}
              placeholder="Find any job: name, job id, client" aria-label="Find any job"
              disabled={Boolean(busy)}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Escape" && q) { e.preventDefault(); setQ(""); } }}
            />
            {!q && !phone && <span className="ijp-kbd" aria-hidden="true">/</span>}
            {q && <button type="button" className="ijp-clear" onClick={() => { setQ(""); searchRef.current?.focus(); }}>Clear</button>}
          </div>
        </div>

        {data && error && (
          <div className="ijp-banner" role="alert">
            The list could not be refreshed ({errorWords(error)}); this is the last one loaded.{" "}
            <button type="button" className="ijp-linkbtn" onClick={() => reload()}>Try again</button>
          </div>
        )}

        <div className="ijp-rulebar" ref={countRef} tabIndex={-1}>
          {busy ? null : term ? `Searching all ${jobs.length} jobs.` : (
            <>
              <span><strong>{shown.length} of {jobs.length} jobs</strong> are in Ingest right now.</span>{" "}
              <button type="button" className="ijp-linkbtn" data-act="rule" aria-expanded={ruleOpen} aria-controls="ijp-rule" onClick={() => setRuleOpen((v) => !v)}>
                {ruleOpen ? "Hide the rule" : "How does a job get here?"}
              </button>
            </>
          )}
        </div>
        {ruleOpen && !term && !busy && <RulePanel screen={screen} />}

        {body}
        {foot}
      </div>
      <Toast toast={toast} onDone={clearToast} />
    </div>
  );
}

/** The error in plain words. */
export function errorWords(e) {
  const code = String(e?.code ?? "");
  if (code.includes("permission-denied")) return e?.message || "This account cannot open the job list.";
  if (code.includes("unauthenticated")) return "Sign in again, then try again.";
  return "The Suite could not reach the jobs. Check the connection and try again; nothing on this screen is lost.";
}

export function RulePanel({ screen }) {
  return (
    <div className="ijp-rule" id="ijp-rule">
      <h2>How a job gets into Ingest</h2>
      A job is in Ingest <b>automatically</b> when any of these is true:
      <ol>
        <li>It has a shoot day in the next {NEAR_AHEAD_DAYS} days or the last {NEAR_BEHIND_DAYS} days.</li>
        <li>It is booked, on set or processing, and something happened on it in the last <b>{QUIET_DAYS} days</b> (an edit, a capture, an upload, processing).</li>
        <li>It still has work open: captures waiting for review, processing not finished or failed, a capture not finished, registration open, or guest pages, which count for the first <b>{GUEST_PAGE_DAYS} days</b> after their email.</li>
      </ol>
      It drops out on its own when none of these is true any more: a booked, on-set or processing job after more than <b>{QUIET_DAYS} quiet days</b> with no shoot day near; a delivered job as soon as it has no shoot day near and nothing left open.
      Being delivered, invoiced or paid does not take a job out while work is still open. Trashed jobs never show.
      <div className="ijp-note"><b>By hand:</b> <i>Pinned</i> keeps a job here until someone unpins it (a pinned job cannot be archived; unpin it first). <i>Archived</i> takes it off this list, for everyone, with Undo for a moment. That is all it does: nothing is deleted, the status (delivered, paid …) does not change, and guest pages, captures and delivery carry on. An archived job comes back on its own when something needs attention (new captures to review, a processing failure), marked “Back: …”; plain activity, like a guest viewing their page, does not bring it back. <i>Automatic</i> goes back to the rule. Every job is always reachable with Find, and the archived ones are listed at the foot of this page.</div>
      <div className="ijp-note">{screen === "handheld" ? "Handheld leaves out guest jobs and the studio walk-ins jobs (they are captured on the rig). " : ""}Event accounts (greeter phones) do not use this list: they see only the jobs they are assigned.</div>
    </div>
  );
}

export { whoOf };
